import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useQuery } from 'urql';
import ModalBase from '../Modal/ModalBase.jsx';
import { Search, AlertTriangle } from '../icons/index.jsx';
import DataTable from '../Table/DataTable.jsx';
import { emitEvent } from '../../api/events.js';
import { Q_CLIENTES } from '@/modules/administracion/clientes/clientes.gql.js';
import './search.css';

// Columnas optimizadas para vista enterprise
const COLUMNS = [
  { 
    key: 'razonSocial', 
    label: 'Razón Social',
    render: (value) => (
      <div style={{ 
        maxWidth: '240px', 
        overflow: 'hidden', 
        textOverflow: 'ellipsis', 
        whiteSpace: 'nowrap',
        fontWeight: 600,
        color: 'var(--modal-fg)'
      }}>
        {value || '—'}
      </div>
    )
  },
  { 
    key: 'cuit', 
    label: 'CUIT',
    render: (value) => (
      <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
        {value || '—'}
      </span>
    )
  },
  { 
    key: 'direccion', 
    label: 'Dirección Completa',
    render: (value, row) => {
      // Formato: "Constitución 3294 · Piso 2 · Dpto D"
      const parts = [];
      if (value) parts.push(value);
      if (row.pisoDepto) {
        // Separar piso y depto si vienen juntos
        const pisoDeptoText = row.pisoDepto.toString();
        if (pisoDeptoText.toLowerCase().includes('piso') || pisoDeptoText.toLowerCase().includes('dpto')) {
          parts.push(pisoDeptoText);
        } else {
          // Asumir formato "2A" o "2 A"
          const match = pisoDeptoText.match(/^(\d+)\s*([A-Za-z]?)$/);
          if (match) {
            if (match[1]) parts.push(`Piso ${match[1]}`);
            if (match[2]) parts.push(`Dpto ${match[2].toUpperCase()}`);
          } else {
            parts.push(pisoDeptoText);
          }
        }
      }
      const direccionCompleta = parts.length > 0 ? parts.join(' · ') : '—';
      
      return (
        <div style={{ 
          maxWidth: '320px', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          color: 'rgba(var(--muted), 0.95)',
          fontSize: '0.9rem'
        }}>
          {direccionCompleta}
        </div>
      );
    }
  },
  { 
    key: 'emails', 
    label: 'Email',
    render: (value) => {
      const email = Array.isArray(value) ? value[0] : value;
      return (
        <div style={{ 
          maxWidth: '180px', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          color: 'rgba(var(--muted), 0.9)',
          fontSize: '0.875rem'
        }}>
          {email || '—'}
        </div>
      );
    }
  },
  { 
    key: 'telefono', 
    label: 'Teléfono',
    render: (value, row) => {
      const tel = value || row.celular || '—';
      return (
        <span style={{ 
          fontFamily: 'monospace', 
          fontSize: '0.875rem',
          color: 'rgba(var(--muted), 0.9)'
        }}>
          {tel}
        </span>
      );
    }
  },
];

/**
 * SearchClientsModal - Modal de búsqueda de clientes con UX enterprise
 * 
 * Características:
 * - Búsqueda global normalizada (razón social, CUIT, email, teléfono, dirección)
 * - Debounce de 400ms para optimizar requests
 * - AbortController para cancelar búsquedas anteriores
 * - Navegación por teclado (↑↓ + Enter)
 * - Focus trap dentro del modal
 * - Estados loading/empty profesionales
 * - Modal ancho (1100px) con scroll interno
 * - Botón "Crear cliente" para alta rápida sin salir del flujo
 */
export default function SearchClientsModal({ isOpen, onClose, onSelect, onOpenCreate, excludeId }) {
  const titleId = useId();
  const inputId = useId();
  const inputRef = useRef(null);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const abortControllerRef = useRef(null);
  
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  // GraphQL query con filtro de búsqueda normalizado
  const [{ data, fetching, error }] = useQuery({
    query: Q_CLIENTES,
    variables: {
      page: 1,
      pageSize: 100, // Enterprise: más resultados
      filter: debouncedSearch.length >= 3 ? { search: debouncedSearch } : undefined,
    },
    pause: !isOpen || debouncedSearch.length < 3,
    requestPolicy: 'network-only', // Siempre traer datos frescos
  });

  const clientes = data?.clientes?.edges?.map(edge => edge.node) ?? [];
  const total = data?.clientes?.totalCount ?? 0;

  // Filtrar clientes excluyendo el excludeId si se proporciona
  const filteredClientes = excludeId
    ? clientes.filter(c => String(c.id) !== String(excludeId))
    : clientes;

  // Debounce del campo de búsqueda (400ms) con abortController
  useEffect(() => {
    // Cancelar request anterior si existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!searchValue.trim() || searchValue.trim().length < 3) {
      setDebouncedSearch('');
      setSelectedIndex(-1);
      return undefined;
    }
    
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValue.trim());
      setSelectedIndex(-1);
      emitEvent('clients.typeahead', {
        search: searchValue.trim(),
      });
    }, 400);
    
    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]);

  // Reset al cerrar
  useEffect(() => {
    if (!isOpen) {
      setSelectedCliente(null);
      setSearchValue('');
      setDebouncedSearch('');
      setSelectedIndex(-1);
      
      // Cancelar requests pendientes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    } else {
      // Auto-focus al abrir
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleChange = event => {
    setSearchValue(event.target.value);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const trimmed = searchValue.trim();
    if (trimmed.length >= 3) {
      setDebouncedSearch(trimmed);
      emitEvent('clients.search', { search: trimmed });
    }
  };

  const handleSelectCliente = useCallback((cliente) => {
    emitEvent('clients.select', cliente);
    if (typeof onSelect === 'function') {
      onSelect(cliente);
      onClose();
    } else {
      setSelectedCliente(cliente);
    }
  }, [onSelect, onClose]);

  const handleRowClick = row => {
    handleSelectCliente(row);
  };

  const handleClose = () => {
    onClose();
  };

  // Navegación por teclado en resultados (↑↓ + Enter)
  const handleKeyDown = useCallback((event) => {
    if (fetching || error || searchValue.trim().length < 3 || searchValue.trim() !== debouncedSearch || filteredClientes.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredClientes.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        if (selectedIndex >= 0 && selectedIndex < filteredClientes.length) {
          event.preventDefault();
          handleSelectCliente(filteredClientes[selectedIndex]);
        }
        break;
      default:
        break;
    }
  }, [filteredClientes, selectedIndex, handleSelectCliente, fetching, error, searchValue, debouncedSearch]);

  // Estados de UI
  const ready = searchValue.trim().length >= 3 && searchValue.trim() === debouncedSearch;
  const showEmptyInitial = searchValue.trim().length < 3;
  const showLoading = !showEmptyInitial && (!ready || fetching);
  const showError = ready && !fetching && error;
  const showEmpty = ready && !fetching && !error && filteredClientes.length === 0;
  const showResults = ready && !fetching && !error && filteredClientes.length > 0;

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={handleClose}
      labelledBy={titleId}
      initialFocusRef={inputRef}
      closeOnBackdropClick={true}
      closeOnEsc={true}
      ariaLabel="Buscar Clientes"
      backdropClassName="modal-backdrop modal-backdrop--search"
      viewportClassName="modal-viewport search-modal__viewport"
      className="modal modal-content search-modal__container focus-visible:outline-none"
    >
      <div className="search-modal__header">
        <div>

          <h2 id={titleId} className="search-modal__title">
            Buscar Clientes
          </h2>

        </div>
        <button type="button" className="search-modal__close" onClick={handleClose}>
          Cerrar
        </button>
      </div>

      {selectedCliente ? (
        <section className="search-modal__detail" aria-label="Datos del cliente">
          <button type="button" className="search-modal__secondary" autoFocus onClick={() => { setSelectedCliente(null); setTimeout(() => inputRef.current?.focus(), 0); }}>
            Volver a resultados
          </button>
          <h3>{selectedCliente.razonSocial || 'Cliente'}</h3>
          <dl className="search-modal__detail-grid">
            {[
              ['Código', selectedCliente.codigo],
              ['Nombre de fantasía', selectedCliente.nombreFantasia],
              ['CUIT', selectedCliente.cuit],
              ['Condición IVA', selectedCliente.condicionIVA],
              ['Email', Array.isArray(selectedCliente.emails) ? selectedCliente.emails.join(', ') : selectedCliente.emails],
              ['Teléfono', selectedCliente.telefono],
              ['Celular', selectedCliente.celular],
              ['Dirección', [selectedCliente.direccion, selectedCliente.pisoDepto, selectedCliente.localidad?.nombre, selectedCliente.provincia?.nombre].filter(Boolean).join(' · ')],
              ['Sucursal responsable', selectedCliente.sucursalResponsableNombre],
              ['Lista de tarifas', selectedCliente.tarifa?.listaTarifaNombre],
            ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || 'No registrado'}</dd></div>)}
          </dl>
          <a className="search-modal__secondary" href={`/admin/clientes/${encodeURIComponent(selectedCliente.id)}`}>Abrir ficha completa</a>
        </section>
      ) : <>
      <form className="search-modal__form search-modal__form--clients" onSubmit={handleSubmit}>
        <div className="search-modal__group">
          <label htmlFor={inputId} className="search-modal__label">Búsqueda Global</label>
          <input
            id={inputId}
            ref={inputRef}
            className="search-modal__input"
            type="text"
            name="search"
            value={searchValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Nombre, CUIT, email o teléfono"
            aria-describedby={`${inputId}-hint`}
            autoComplete="off"
            spellCheck="false"
          />
        </div>
        
        <div className="search-modal__actions">
          <button
            type="submit"
            className="search-modal__primary"
            disabled={fetching || searchValue.trim().length < 3}
            aria-label="Ejecutar búsqueda de clientes"
          >
            <Search size={18} />
            <span>{fetching ? 'Buscando...' : 'Buscar'}</span>

          </button>
          
          {onOpenCreate && (
            <button
              type="button"
              className="search-modal__secondary"
              onClick={onOpenCreate}
              aria-label="Crear nuevo cliente"
            >
              <span>+ Crear Cliente</span>
            </button>
          )}
        </div>
        <p id={`${inputId}-hint`} className="search-modal__helper">Ingresá al menos 3 caracteres.</p>
      </form>

      <div className="search-modal__table-wrapper">


        {/* Estado: Cargando */}
        {showLoading && (
          <div className="search-modal__loading" role="status">
            <div className="search-modal__spinner" />
            <span>Buscando clientes...</span>
          </div>
        )}

        {/* Estado: Sin resultados */}
        {showEmpty && (
          <div className="search-modal__empty-state">
            <div className="search-modal__empty-state-icon">
              <AlertTriangle size={48} />
            </div>
            <p className="search-modal__empty-state-title">
              No se encontraron clientes
            </p>
            <p className="search-modal__empty-state-hint">
              Probá con otro término: CUIT completo, email, o parte de la dirección
            </p>
          </div>
        )}

        {/* Estado: Resultados */}
        {showResults && (
          <>
            <p className="search-modal__summary">
              {filteredClientes.length} cliente{filteredClientes.length !== 1 ? 's' : ''} encontrado{filteredClientes.length !== 1 ? 's' : ''}
              {excludeId && clientes.length !== filteredClientes.length && ' (excluyendo cliente ya seleccionado)'}
            </p>
            <DataTable columns={COLUMNS} rows={filteredClientes} onRowClick={handleRowClick} pageSize={20} />
            <p className="search-modal__hint">
              Usá Tab para enfocar un resultado y Enter para abrirlo.
            </p>
          </>
        )}

        {/* Estado: Error */}
        {showError && (
          <div className="search-modal__empty-state" role="alert">
            <div className="search-modal__empty-state-icon">
              <AlertTriangle size={48} />
            </div>
            <p className="search-modal__empty-state-title">
              Error al buscar clientes
            </p>
            <p className="search-modal__empty-state-hint">
              {error.message || 'Ocurrió un error al realizar la búsqueda'}
            </p>
          </div>
        )}
      </div>
      </>}
    </ModalBase>
  );
}
